import { Component, computed, effect, inject, input, output, resource } from "@angular/core";
import { VIRTUAL_SCROLL_STRATEGY } from "@angular/cdk/scrolling";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { IiFirmUserRolesWithUsersMap } from "@app/interfaces/ifirm-roles.interfaces";
import { UserDataModel } from "@app/settings-interfaces";
import { JobsBillingCard, type ColumnFilterSelection, type IColumnData } from "@app/views-interfaces";
import { IMergedStatusObjectModel } from "@app/views-interfaces/status-object-model.interface";
import { CardDisplayService } from "@shared/card-display";
import { FiltersService, IFirmRolesService, isUnassigned, UnassignedUserId, UsersService } from "@shared/index";
import { AssigneesColumnScrollStrategy, VIRTUAL_SCROLL_THRESHOLD } from "./assignees-column-scroll-strategy";
import { ColumnStateService } from "./column-state.service";

interface IColumnsResourceParams {
    status: IMergedStatusObjectModel;
    users: Map<string, UserDataModel> | undefined;
    rolesWithUsers: IiFirmUserRolesWithUsersMap;
}

@Component({
    selector: "jobs-assignees-columns-wrapper",
    templateUrl: "./jobs-assignees-columns-wrapper.component.html",
    styleUrl: "./assignees-columns-wrapper.component.scss",
    standalone: false,
    host: { class: "wk-ac-wrapper fancy-scrollbar-newUI", id: "virtual-scroller-wrapper" },
    providers: [ColumnStateService, AssigneesColumnScrollStrategy, { provide: VIRTUAL_SCROLL_STRATEGY, useExisting: AssigneesColumnScrollStrategy }],
})
export class JobsAssigneesColumnsWrapperComponent {
    private readonly usersService = inject(UsersService);
    private readonly cardDisplayService = inject(CardDisplayService);
    private readonly iFirmRolesService = inject(IFirmRolesService);
    private readonly filtersService = inject(FiltersService);
    private readonly strategy = inject(AssigneesColumnScrollStrategy);

    readonly isInitialized = input<boolean>(true);
    readonly selectedStatus = input.required<IMergedStatusObjectModel>();
    readonly selectedStepId = input.required<string>();
    readonly columnFilters = input<ColumnFilterSelection>();
    readonly displayedColumnsCountChange = output<number>();

    private readonly columnsResource = resource<IColumnData<JobsBillingCard>[], IColumnsResourceParams>({
        defaultValue: [],
        params: () => ({
            status: this.selectedStatus(),
            users: this.usersService.usersById()?.fromUserId,
            rolesWithUsers: this.iFirmRolesService.rolesWithUsers(),
        }),
        loader: ({ params }) => this.loadColumns(params),
    });

    protected readonly columns = computed<IColumnData<JobsBillingCard>[]>(() => {
        const allColumns = this.columnsResource.value() ?? [];
        if (allColumns.length === 0) return allColumns;
        const users = this.usersService.usersById()?.fromUserId;
        const columnFilters = this.columnFilters();
        const rolesWithUsers = this.iFirmRolesService.rolesWithUsers();
        return this.applyColumnLevelFilters(allColumns, users, columnFilters, rolesWithUsers);
    });
    protected readonly noCards = computed<boolean>(() => this.columns().reduce((acc, column) => acc + column.cards.length, 0) === 0);
    protected readonly dragDisabled = computed<boolean>(() => this.columns().length > VIRTUAL_SCROLL_THRESHOLD);
    protected readonly virtualScrollThreshold = VIRTUAL_SCROLL_THRESHOLD;

    constructor() {
        this.filtersService.filtersChangedEvent$.pipe(takeUntilDestroyed()).subscribe(() => {
            this.columnsResource.reload(); // TODO refine this, we should just update cards with filter, not re-generate them
        });

        this.cardDisplayService.cardChangedEvent$.pipe(takeUntilDestroyed()).subscribe(() => {
            this.columnsResource.reload();
        });

        effect(() => {
            const ids = this.columns().map((col) => col.id);
            this.strategy.setColumnIds(ids);
        });

        effect(() => {
            this.displayedColumnsCountChange.emit(this.columns().length);
        });
    }

    private async loadColumns(params: IColumnsResourceParams): Promise<IColumnData<JobsBillingCard>[]> {
        const { status, users, rolesWithUsers } = params;
        const statusId = status.TaxReturnStatus?.Id;
        if (!statusId) return [];

        const cardModels = await this.cardDisplayService.getModel();
        const applicableModel = cardModels?.find((model) => model.statusId === statusId);
        if (!applicableModel) return [];

        return this.buildColumns(applicableModel.cards as JobsBillingCard[], status, users, rolesWithUsers);
    }

    private buildColumns(
        jobCards: JobsBillingCard[],
        status: IMergedStatusObjectModel,
        users: Map<string, UserDataModel> | undefined,
        rolesWithUsers: IiFirmUserRolesWithUsersMap,
    ): IColumnData<JobsBillingCard>[] {
        const statusId = status.TaxReturnStatus.Id;

        // Card-level filtering and sorting must happen here (not in ColumnComponent) so that
        // column ordering reflects the number of cards actually shown.
        const filteredCards = this.filtersService.filterCards(jobCards, statusId ? [statusId] : undefined) as JobsBillingCard[];
        this.filtersService.sortCards(filteredCards);

        const cardsByResponsible = new Map<string, JobsBillingCard[]>();
        const unassignedCards: JobsBillingCard[] = [];
        for (const card of filteredCards) {
            const responsible = card.job?.currentlyResponsible;
            if (isUnassigned(responsible)) {
                unassignedCards.push(card);
                continue;
            }
            const bucket = cardsByResponsible.get(responsible);
            if (bucket) {
                bucket.push(card);
            } else {
                cardsByResponsible.set(responsible, [card]);
            }
        }

        const unassignedColumn: IColumnData<JobsBillingCard> = {
            id: UnassignedUserId,
            columnName: "General.UnassignedFem",
            statuses: [status],
            state: null,
            isCustomStatus: null,
            fromCache: null,
            cards: unassignedCards,
            active: true,
            assignedUserId: null,
        };

        if (!users || users.size === 0) return [unassignedColumn];

        const userColumns: IColumnData<JobsBillingCard>[] = [];
        for (const user of users.values()) {
            const userCards = cardsByResponsible.get(user.IFirmUserId) ?? [];

            if (!user.Active && userCards.length === 0) continue;

            userColumns.push({
                id: user.IFirmUserId,
                columnName: user.FullName,
                statuses: [status],
                state: null,
                isCustomStatus: null,
                fromCache: null,
                cards: userCards,
                active: user.Active,
                assignedUserId: user.IFirmUserId,
            });
        }
        userColumns.sort((a, b) => {
            if (a.active !== b.active) return a.active ? 1 : -1;
            if (b.cards.length !== a.cards.length) return b.cards.length - a.cards.length;
            if (a.columnName !== b.columnName) return a.columnName.localeCompare(b.columnName);
            return 0;
        });

        const filteredColumns = [unassignedColumn, ...userColumns];
        return filteredColumns;
    }

    private applyColumnLevelFilters(
        columns: IColumnData<JobsBillingCard>[],
        users: Map<string, UserDataModel> | undefined,
        columnFilters: ColumnFilterSelection | undefined,
        rolesWithUsers: IiFirmUserRolesWithUsersMap,
    ): IColumnData<JobsBillingCard>[] {
        if (!columnFilters) return columns;

        const hasRolesFilter = columnFilters.roles.length > 0;
        const hasUsersFilter = columnFilters.users.length > 0;
        const hasGroup1Filter = columnFilters.userGroup1.length > 0;
        const hasGroup2Filter = columnFilters.userGroup2.length > 0;

        if (!hasRolesFilter && !hasUsersFilter && !hasGroup1Filter && !hasGroup2Filter) {
            return columns;
        }

        let usersInSelectedRoles: Set<string> | null = null;

        if (hasRolesFilter) {
            usersInSelectedRoles = new Set<string>();
            const selectedRoleIds = columnFilters.roles.map((r) => r.value as string);

            for (const roleId of selectedRoleIds) {
                const roleData = rolesWithUsers.get(roleId);
                if (roleData?.Users) {
                    for (const user of roleData.Users.values()) {
                        if (user.Active) {
                            usersInSelectedRoles.add(user.IFirmUserId);
                        }
                    }
                }
            }
        }

        const selectedUserIds = hasUsersFilter ? new Set(columnFilters.users.map((u) => u.value as string)) : null;
        const selectedGroup1Values = hasGroup1Filter ? new Set(columnFilters.userGroup1.map((g) => g.value as string)) : null;
        const selectedGroup2Values = hasGroup2Filter ? new Set(columnFilters.userGroup2.map((g) => g.value as string)) : null;

        return columns.filter((column) => {
            if (column.id === UnassignedUserId) return true;

            const assignedUserId = column.assignedUserId;
            if (!assignedUserId) return true;

            const userData = users?.get(assignedUserId);

            if (usersInSelectedRoles && !usersInSelectedRoles.has(assignedUserId)) {
                return false;
            }

            if (selectedUserIds) {
                const matchesUser = selectedUserIds.has(assignedUserId) || (userData?.Id && selectedUserIds.has(userData.Id));
                if (!matchesUser) return false;
            }

            if (selectedGroup1Values && userData) {
                const userGroup1 = userData.GroupFilteringId1?.toString() ?? "-1";
                if (!selectedGroup1Values.has(userGroup1)) return false;
            }

            if (selectedGroup2Values && userData) {
                const userGroup2 = userData.GroupFilteringId2?.toString() ?? "-1";
                if (!selectedGroup2Values.has(userGroup2)) return false;
            }

            return true;
        });
    }

    protected trackByColumnId(_index: number, column: IColumnData<JobsBillingCard>): string {
        return column.id;
    }
}
